/**
 * Action Inbox API client.
 *
 * Endpoints proxied:
 *   GET    /v1/actions[?status=...]
 *   POST   /v1/actions/draft-now
 *   PATCH  /v1/actions/:id
 *   POST   /v1/actions/:id/approve
 *   POST   /v1/actions/:id/reject
 *   POST   /v1/actions/:id/cancel
 */

import { supabase } from './supabase';
import { readCurrentScope } from './hooks/view-scope';
import { readImpersonationTarget } from './impersonation';
import { getDemoAccountId } from '@/lib/utils/demo-mode';

const API_URL =
  import.meta.env.VITE_API_URL ??
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:8787'
    : 'https://api.waypoint-intel.com');

/**
 * Build the full header set including scope/impersonation/demo-override.
 * Mirrors what lib/api.ts api() does so every actions endpoint respects
 * the current view context (demo mode, scope picker, impersonation).
 * Without this, listActions in demo mode hits the user's REAL account.
 */
async function authHeader(): Promise<Record<string, string>> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  const headers: Record<string, string> = {
    Authorization: `Bearer ${session.access_token}`,
    'X-Waypoint-Scope': readCurrentScope(),
  };
  const impersonate = readImpersonationTarget();
  if (impersonate) headers['X-Waypoint-Impersonate'] = impersonate.userId;
  const demoAccountId = getDemoAccountId();
  if (demoAccountId) headers['X-Waypoint-Account-Override'] = demoAccountId;
  return headers;
}

export type ActionStatus =
  | 'proposed'
  | 'approved'
  | 'executing'
  | 'executed'
  | 'failed'
  | 'rejected'
  | 'cancelled'
  | 'completed'; // chat-agent notes (user_note) are saved already-completed

export type ActionKind =
  | 'gmail_followup_draft'
  | 'mark_signal_resolved'
  | 'add_action_item'
  | 'log_decision'
  | 'create_win'
  | 'snooze_signal'
  // Specialist agents from agent_plans
  | 'ai_drafted_email'
  | 'ai_proposed_meeting'
  | 'update_crm_stage'
  | 'ai_escalation'
  | 'ai_research_finding'
  | 'ai_investigation'
  | 'ai_logged_note'
  // Autonomous overnight agent
  | 'autonomous_follow_up_email'
  | 'autonomous_check_in_email'
  | 'autonomous_internal_note'
  | 'autonomous_escalation_to_partner'
  // Catch-all for forward compatibility
  | (string & {});

export interface ActionRow {
  id: string;
  kind: ActionKind;
  status: ActionStatus;
  severity: 'low' | 'med' | 'high';
  title: string;
  preview: string | null;
  payload: Record<string, unknown>;
  proposed_by: 'agent' | 'cron' | 'user' | 'trigger';
  proposed_at: string;
  created_at?: string;          // server uses created_at on newer kinds
  approved_at: string | null;
  executed_at: string | null;
  failed_at: string | null;
  rejected_at: string | null;
  rejected_reason: string | null;
  result: Record<string, unknown> | null;
  error_message: string | null;
  source_signal_id: string | null;
  source_thread_id: string | null;
  /** Thread context the API joins for drafts replying to a synced email. */
  thread?: { subject: string; snippet: string | null; last_sender_email: string | null } | null;
  /** Where to open that thread in the mailbox that holds it (Gmail or Outlook on the web). */
  threadUrl?: string | null;
  /** Set by the API on an open action that acts outside Waypoint but cannot run yet: why. */
  unavailable?: { code: string; reason: string };
}

export async function listActions(status?: string): Promise<ActionRow[]> {
  const headers = await authHeader();
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  const res = await fetch(`${API_URL}/v1/actions${qs}`, { headers });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`API ${res.status}: ${body.slice(0, 200)}`);
  }
  const data = (await res.json()) as { actions: ActionRow[] };
  return data.actions ?? [];
}

export async function draftActionsNow(): Promise<{
  ok: boolean;
  drafted: number;
  skipped: number;
  errors: string[];
  skipReasons?: string[];
}> {
  const headers = await authHeader();
  const res = await fetch(`${API_URL}/v1/actions/draft-now`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`API ${res.status}: ${body.slice(0, 200)}`);
  }
  return await res.json();
}

export async function patchAction(
  id: string,
  patch: { title?: string; preview?: string; payload?: Record<string, unknown> }
): Promise<ActionRow> {
  const headers = await authHeader();
  const res = await fetch(`${API_URL}/v1/actions/${id}`, {
    method: 'PATCH',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`API ${res.status}: ${body.slice(0, 200)}`);
  }
  const data = (await res.json()) as { action: ActionRow };
  // An edited draft is the sharpest quality signal there is — "nearly right".
  // Without this, metrics read 100% sent and never show that users rewrite
  // every draft before sending it. (edit_distance is left for when it is asked
  // for: it needs the pre-edit text, which this caller does not hold.)
  if (patch.preview !== undefined || patch.title !== undefined) rateAiAction(data.action, 'edited');
  return data.action;
}

/**
 * Error thrown by approveAction when the API returns a structured failure
 * reason (e.g. insufficient_scope → user must reconnect Gmail).
 */
export class ApproveActionError extends Error {
  reason: string | null;
  status: number;
  constructor(message: string, status: number, reason: string | null) {
    super(message);
    this.status = status;
    this.reason = reason;
  }
}

/**
 * Rate the AI draft behind an action once the human has decided on it.
 *
 * Hooked here rather than in the components because approveAction/rejectAction
 * are what every surface calls (ActionInbox, ActionFeedCard, Actions hub,
 * MobileBriefing, HeaderIndicators) — one hook covers all of them, and a new
 * caller is covered for free.
 *
 * Fire-and-forget by design: rating an output must never fail or delay the
 * action the user actually asked for. `ai_output_id` is set by the drafter;
 * without it the API resolves the output from (kind, related_entity_id).
 */
function rateAiAction(row: ActionRow, action: 'sent' | 'rejected' | 'edited'): void {
  if (row.proposed_by === 'user') return;   // human-written: nothing AI to rate
  const outputId = row.payload?.ai_output_id;
  void (async () => {
    try {
      const headers = await authHeader();
      await fetch(`${API_URL}/v1/ai/feedback`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(
          typeof outputId === 'string'
            ? { output_id: outputId, action }
            : { kind: row.kind, related_entity_id: row.id, action },
        ),
      });
    } catch {
      // Telemetry only — a lost rating is not worth surfacing to the user.
    }
  })();
}

export async function approveAction(id: string): Promise<ActionRow> {
  const headers = await authHeader();
  const res = await fetch(`${API_URL}/v1/actions/${id}/approve`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) {
    let reason: string | null = null;
    let message = `API ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string; reason?: string };
      if (typeof body.reason === 'string') reason = body.reason;
      if (typeof body.error === 'string') message = body.error;
    } catch {
      const text = await res.text().catch(() => '');
      if (text) message = `API ${res.status}: ${text.slice(0, 200)}`;
    }
    throw new ApproveActionError(message, res.status, reason);
  }
  const data = (await res.json()) as { action: ActionRow };
  rateAiAction(data.action, 'sent');
  return data.action;
}

export async function retryAction(id: string): Promise<ActionRow> {
  const headers = await authHeader();
  const res = await fetch(`${API_URL}/v1/actions/${id}/retry`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`API ${res.status}: ${body.slice(0, 200)}`);
  }
  const data = (await res.json()) as { action: ActionRow };
  return data.action;
}

/** proposed | approved | failed → cancelled. The inbox's Dismiss on a failed send. */
export async function cancelAction(id: string): Promise<ActionRow> {
  const headers = await authHeader();
  const res = await fetch(`${API_URL}/v1/actions/${id}/cancel`, { method: 'POST', headers });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`API ${res.status}: ${body.slice(0, 200)}`);
  }
  const data = (await res.json()) as { action: ActionRow };
  return data.action;
}

export async function rejectAction(id: string, reason?: string): Promise<ActionRow> {
  const headers = await authHeader();
  const res = await fetch(`${API_URL}/v1/actions/${id}/reject`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason: reason ?? null }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`API ${res.status}: ${body.slice(0, 200)}`);
  }
  const data = (await res.json()) as { action: ActionRow };
  rateAiAction(data.action, 'rejected');
  return data.action;
}
